import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ChevronLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { adminAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import Spinner from '../components/Spinner'
import { apiErrorMessage } from '../utils/apiError'
import type {
  ExitBacktestReport,
  ExitPairedComparison,
  ExitRuleKey,
  ExitRuleSummary,
} from '../types/exitBacktest'

/**
 * Admin-only, read-only exit-strategy backtest.
 *
 * Replays every closed real engine signal from its recorded entry under six
 * exit rules on real Kraken candles and compares them. It tests EXITS on
 * FIXED ENTRIES: a better exit cannot create entry edge, so the page is
 * framed as a variance / drawdown comparison and never as a path to profit.
 * The backend writes nothing; the route is gated to superusers in App.tsx
 * and is linked only from the admin Engine page, never from anything a
 * subscriber sees.
 */

// Fixed categorical slot per rule (dataviz palette, slots 1-6). Colour
// follows the rule, never its rank, so a filtered chart keeps its hues.
const RULE_COLOR: Record<ExitRuleKey, string> = {
  fixed: '#2a78d6',
  breakeven: '#eb6834',
  trailing_atr: '#1baf7a',
  target_only: '#eda100',
  partial_breakeven: '#8e5bd0',
  partial_only: '#d14d8b',
}

const RULE_ORDER: ExitRuleKey[] = [
  'fixed',
  'breakeven',
  'trailing_atr',
  'target_only',
  'partial_breakeven',
  'partial_only',
]

const DEFAULT_BUCKETS = ['long-4h', 'long-1d', 'short-4h', 'short-1d']

const ATR_CHOICES = [2, 2.5, 3, 3.5, 4, 4.5, 5]

const fmtR = (v: number | null | undefined, digits = 2) =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(digits)}R`

const fmtPct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(0)}%`

const fmtCI = (s: { ci95_low: number | null; ci95_high: number | null }) =>
  s.ci95_low === null || s.ci95_high === null
    ? '—'
    : `${s.ci95_low >= 0 ? '+' : ''}${s.ci95_low.toFixed(2)} to ${s.ci95_high >= 0 ? '+' : ''}${s.ci95_high.toFixed(2)}`

const reasonLabel: Record<string, string> = {
  target: 'target',
  stop: 'stop',
  breakeven_stop: 'breakeven stop',
  trailing_stop: 'trailing stop',
  expired: 'expired',
  partial_target: 'half off, then target',
  partial_stop: 'half off, then stop',
  partial_breakeven_stop: 'half off, then breakeven stop',
  partial_expired: 'half off, then expired',
  path_end: 'no exit (path ended)',
}

const fmtShare = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(0)}%`

/** "vs current (paired)" cells for one rule; Fixed and the record get dashes. */
const PairedCells = ({ p }: { p: ExitPairedComparison | null | undefined }) => {
  if (!p) {
    return (
      <>
        <td className="px-2 py-2 text-right text-gray-400">—</td>
        <td className="px-2 py-2 text-right text-gray-400">—</td>
        <td className="px-2 py-2 text-gray-400">—</td>
        <td className="px-2 py-2 text-right text-gray-400">—</td>
        <td className="px-2 py-2 text-right text-gray-400">—</td>
      </>
    )
  }
  return (
    <>
      <td className="px-2 py-2 text-right tabular-nums">{fmtR(p.mean_diff_r)}</td>
      <td className="px-2 py-2 text-right tabular-nums">{fmtCI(p)}</td>
      <td className="px-2 py-2">
        <SignificanceBadge significant={p.significant} n={p.n} />
      </td>
      <td className="px-2 py-2 text-right tabular-nums" title={`${p.better} better · ${p.worse} worse`}>
        {p.changed} of {p.n}
      </td>
      <td
        className="px-2 py-2 text-right tabular-nums"
        title={p.top_signal_ids.length ? `signals ${p.top_signal_ids.join(', ')}` : undefined}
      >
        {fmtShare(p.top_share)}
      </td>
    </>
  )
}

const fmtReasons = (reasons: Record<string, number>) =>
  Object.entries(reasons)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${n} ${reasonLabel[k] ?? k}`)
    .join(' · ') || '—'

type CurveRow = { index: number } & Partial<Record<ExitRuleKey, number>>

/** Aligns the per-rule cumulative curves on trade index (same entries across rules). */
const buildCurveRows = (report: ExitBacktestReport): CurveRow[] => {
  const length = Math.max(...RULE_ORDER.map((r) => report.curves[r]?.length ?? 0), 0)
  const rows: CurveRow[] = []
  for (let i = 0; i < length; i++) {
    const row: CurveRow = { index: i }
    for (const rule of RULE_ORDER) {
      const point = report.curves[rule]?.[i]
      if (point) row[rule] = Number(point.cum_r.toFixed(3))
    }
    rows.push(row)
  }
  return rows
}

const AdminExitBacktest = () => {
  const { user } = useAuthStore()
  const [atrMultiple, setAtrMultiple] = useState(3)
  const [report, setReport] = useState<ExitBacktestReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSignals, setShowSignals] = useState(false)
  const [showData, setShowData] = useState(false)
  const [showBreakdown, setShowBreakdown] = useState(false)

  const load = useCallback(async (multiple: number) => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminAPI.exitBacktest(multiple)
      setReport(data)
    } catch (err) {
      setError(apiErrorMessage(err, 'The backtest could not be run.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user?.is_superuser) load(atrMultiple)
    // Re-run only on an explicit ATR change; the effect key is the multiple.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_superuser, atrMultiple])

  if (!user?.is_superuser) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <ShieldExclamationIcon className="h-10 w-10 text-danger-500 mx-auto mb-3" />
        <h1 className="text-lg font-semibold text-gray-900">Access denied</h1>
        <p className="text-sm text-gray-500 mt-1">You don&apos;t have permission to view this page.</p>
      </div>
    )
  }

  const rulesByKey: Partial<Record<ExitRuleKey, ExitRuleSummary>> = {}
  report?.rules.forEach((r) => {
    rulesByKey[r.rule] = r
  })
  const curveRows = report ? buildCurveRows(report) : []
  const controlOk = report?.control.ok ?? false

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <Link to="/settings/engine" className="inline-flex items-center gap-1 text-sm text-accent hover:underline">
          <ChevronLeftIcon className="h-4 w-4" /> Engine
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-gray-900">Exit-strategy backtest</h1>
        <p className="mt-1 text-sm text-gray-500">
          Admin analysis. Replays the engine&apos;s closed signals under alternative exit rules on real
          Kraken candles. Reads only; changes nothing.
        </p>
      </div>

      <section
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        role="note"
        aria-label="What this analysis is and is not"
      >
        <p className="font-semibold">Historical analysis of exit choice, not a claim of future profit.</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            It tests <strong>exits on fixed entries</strong>. A better exit cannot create entry edge; it can
            only change the variance and drawdown of the same entries.
          </li>
          <li>
            A positive average R that is <strong>not statistically significant</strong> is shown as not
            significant. It is never an edge.
          </li>
          <li>
            The control rule (fixed target/stop) must roughly reproduce the live record. If it does not, the
            price path or the logic is wrong and the other rules cannot be trusted.
          </li>
          <li>Never shown to subscribers. Not financial advice.</li>
        </ul>
      </section>

      <section className="flex flex-wrap items-end gap-4 rounded-lg border border-gray-200 bg-white p-4" aria-label="Parameters">
        <label className="text-sm">
          <span className="block text-gray-500">Trailing-stop ATR multiple</span>
          <select
            className="mt-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-gray-900"
            value={atrMultiple}
            onChange={(e) => setAtrMultiple(Number(e.target.value))}
            disabled={loading}
            aria-label="Trailing-stop ATR multiple"
          >
            {ATR_CHOICES.map((m) => (
              <option key={m} value={m}>
                {m}× ATR{m === 3 ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => load(atrMultiple)}
          disabled={loading}
          className="px-4 py-2 bg-accent text-terminal-bg font-semibold rounded-md hover:bg-accent-hover disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Spinner size="sm" />}
          {loading ? 'Replaying…' : 'Re-run'}
        </button>
        {report && (
          <p className="text-xs text-gray-500">
            Generated {new Date(report.generated_at).toLocaleString()} · venue {report.exchange} · costs:{' '}
            <span data-testid="exit-backtest-costs">{report.costs.label}</span> (
            {(report.costs.fee_rate_per_side * 100).toFixed(2)}% fee + {(report.costs.slippage_rate_per_side * 100).toFixed(2)}%
            slippage per side), expressed in R
            {report.costs.split_exit_round_trip_pct_of_entry !== undefined && (
              <>
                {' '}
                · split exit (half off at step 1):{' '}
                <span data-testid="exit-backtest-split-costs">
                  {report.costs.split_exit_round_trip_pct_of_entry.toFixed(2)}% round trip
                </span>
                , the same fees plus one extra slippage on half the position
              </>
            )}
          </p>
        )}
      </section>

      {error && (
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700" role="alert">
          {error}
        </div>
      )}

      {loading && !report && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner size="sm" /> Fetching candle paths and replaying…
        </div>
      )}

      {report && (
        <>
          {/* Control check */}
          <section
            className={`rounded-lg border p-4 text-sm ${
              controlOk ? 'border-gray-200 bg-white' : 'border-danger-300 bg-danger-50'
            }`}
            aria-label="Control check"
          >
            <div className="flex items-start gap-2">
              {controlOk ? (
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
              ) : (
                <XCircleIcon className="h-5 w-5 flex-shrink-0 text-danger-600" aria-hidden="true" />
              )}
              <div className="space-y-1">
                <p className="font-semibold text-gray-900">
                  {controlOk
                    ? 'Control passed: the fixed target/stop replay reproduces the live record.'
                    : 'Control FAILED: the replay does not reproduce the live record.'}
                </p>
                <p className="text-gray-600">
                  {report.signals.replayed} of {report.signals.closed_real} closed real signals replayed
                  {report.control.reason_match_rate !== null &&
                    ` · exit reason matched on ${fmtPct(report.control.reason_match_rate)} of ${
                      report.control.with_recorded_reason ?? report.control.compared
                    }`}
                  {report.control.mean_abs_r_diff !== null &&
                    ` · mean |ΔR| ${report.control.mean_abs_r_diff.toFixed(2)} (max ${report.control.max_abs_r_diff?.toFixed(2)})`}
                </p>
                <ul className="list-disc space-y-0.5 pl-5 text-gray-600">
                  {report.control.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {!controlOk && report.signals.replayed > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <p>
                The rule comparison below is shown for debugging only. Do not draw conclusions from it until the
                control passes.
              </p>
            </div>
          )}

          {/* Comparison table */}
          <section className="rounded-lg border border-gray-200 bg-white" aria-label="Rule comparison">
            <div className="border-b border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900">Rule comparison</h2>
              <p className="text-sm text-gray-500">
                Same entries under every rule. R is net of costs. Win = net R above zero. Drawdown is the largest
                peak-to-trough fall of cumulative R in entry order.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th colSpan={10} />
                    <th colSpan={5} className="border-l border-gray-200 px-2 pt-2 text-center normal-case text-gray-700">
                      vs current (paired)
                    </th>
                  </tr>
                  <tr>
                    <th className="px-4 py-2">Rule</th>
                    <th className="px-2 py-2 text-right">n</th>
                    <th className="px-2 py-2 text-right">Win rate</th>
                    <th className="px-2 py-2 text-right">Avg R (net)</th>
                    <th className="px-2 py-2 text-right">Median R</th>
                    <th className="px-2 py-2 text-right">Total R</th>
                    <th className="px-2 py-2 text-right">Max DD</th>
                    <th className="px-2 py-2 text-right">95% CI on mean</th>
                    <th className="px-2 py-2">Significance</th>
                    <th className="px-2 py-2">Exits</th>
                    <th className="border-l border-gray-200 px-2 py-2 text-right">Mean ΔR</th>
                    <th className="px-2 py-2 text-right">95% CI on ΔR</th>
                    <th className="px-2 py-2">Significance</th>
                    <th className="px-2 py-2 text-right">Changed</th>
                    <th className="px-2 py-2 text-right" title="Share of the total difference from the 5 largest per-signal differences">
                      Top-5 share
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="text-gray-700">
                    <td className="px-4 py-2 font-medium text-gray-900">
                      Recorded outcomes
                      <span className="block text-xs font-normal text-gray-500">the live record, net of costs</span>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{report.recorded.n}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtPct(report.recorded.win_rate)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtR(report.recorded.avg_r)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtR(report.recorded.median_r)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtR(report.recorded.total_r)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtR(-report.recorded.max_drawdown_r)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{fmtCI(report.recorded)}</td>
                    <td className="px-2 py-2">
                      <SignificanceBadge significant={report.recorded.significant} n={report.recorded.n} />
                    </td>
                    <td className="px-2 py-2 text-gray-500">—</td>
                    <PairedCells p={null} />
                  </tr>
                  {RULE_ORDER.map((key) => {
                    const r = rulesByKey[key]
                    if (!r) return null
                    return (
                      <tr key={key} className="text-gray-700 align-top" data-testid={`rule-row-${key}`}>
                        <td className="px-4 py-2">
                          <span className="flex items-center gap-2 font-medium text-gray-900">
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: RULE_COLOR[key] }}
                              aria-hidden="true"
                            />
                            {r.label}
                            {key === 'trailing_atr' && (
                              <span className="text-xs font-normal text-gray-500">{report.atr_multiple}× ATR{report.atr_period}</span>
                            )}
                          </span>
                          <span className="block max-w-xs text-xs text-gray-500">{r.verdict}</span>
                          {(r.ambiguous_candles > 0 || r.unscored_path_end > 0) && (
                            <span className="block text-xs text-gray-500">
                              {r.ambiguous_candles > 0 && `${r.ambiguous_candles} ambiguous candle(s) scored stop-first`}
                              {r.ambiguous_candles > 0 && r.unscored_path_end > 0 && ' · '}
                              {r.unscored_path_end > 0 && `${r.unscored_path_end} unscored (path ended)`}
                            </span>
                          )}
                          {(r.step1_fallbacks ?? 0) > 0 && (
                            <span className="block text-xs text-gray-500" data-testid={`step1-fallbacks-${key}`}>
                              {r.step1_fallbacks} without their own step 1, scored at entry ± 1R
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">{r.n}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtPct(r.win_rate)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtR(r.avg_r)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtR(r.median_r)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtR(r.total_r)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtR(-r.max_drawdown_r)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtCI(r)}</td>
                        <td className="px-2 py-2">
                          <SignificanceBadge significant={r.significant} n={r.n} />
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-500">{fmtReasons(r.exit_reasons)}</td>
                        <PairedCells p={key === 'fixed' ? null : r.vs_fixed} />
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Direction x timeframe breakdown */}
          <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm" aria-label="Breakdown by direction and timeframe">
            <button
              type="button"
              className="font-semibold text-gray-900 hover:underline"
              onClick={() => setShowBreakdown((v) => !v)}
              aria-expanded={showBreakdown}
            >
              {showBreakdown ? 'Hide' : 'Show'} breakdown by direction and timeframe
            </button>
            {showBreakdown && (
              <div className="mt-3 overflow-x-auto">
                {!controlOk && (
                  <p className="mb-2 text-xs text-amber-900">Control failed: shown for debugging only.</p>
                )}
                {(() => {
                  const buckets = [...(report.breakdown_buckets ?? DEFAULT_BUCKETS)]
                  if (RULE_ORDER.some((k) => (rulesByKey[k]?.breakdown?.other?.n ?? 0) > 0)) buckets.push('other')
                  return (
                    <table className="min-w-full text-xs" data-testid="exit-backtest-breakdown">
                      <thead className="text-left uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-2 py-1">Rule</th>
                          {buckets.map((b) => (
                            <th key={b} className="px-2 py-1 text-right">
                              {b}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-gray-700">
                        {RULE_ORDER.map((key) => {
                          const r = rulesByKey[key]
                          if (!r) return null
                          return (
                            <tr key={key}>
                              <td className="px-2 py-1 whitespace-nowrap">{r.label}</td>
                              {buckets.map((b) => {
                                const cell = r.breakdown?.[b]
                                return (
                                  <td key={b} className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
                                    {cell && cell.n > 0 ? `${fmtR(cell.avg_r)} (n ${cell.n})` : '—'}
                                  </td>
                                )
                              })}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )
                })()}
                <p className="mt-2 text-xs text-gray-500">Average net R per signal, with the number of scored signals.</p>
              </div>
            )}
          </section>

          {/* Cumulative R curves */}
          <section className="rounded-lg border border-gray-200 bg-white p-4" aria-label="Cumulative R by rule">
            <h2 className="text-lg font-semibold text-gray-900">Cumulative net R by rule</h2>
            <p className="mb-3 text-sm text-gray-500">
              Trades in entry order, each rule on the same entries. Unsized sum of per-trade R, not an account
              return.
            </p>
            {curveRows.length > 1 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={curveRows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="index"
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      axisLine={{ stroke: '#d1d5db' }}
                      tickLine={false}
                      label={{ value: 'trade #', position: 'insideBottomRight', offset: -4, fontSize: 11, fill: '#6b7280' }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      axisLine={false}
                      tickLine={false}
                      width={48}
                      tickFormatter={(v: number) => `${v}R`}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [fmtR(value), rulesByKey[name as ExitRuleKey]?.label ?? name]}
                      labelFormatter={(label) => `after trade ${label}`}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend
                      formatter={(value: string) => rulesByKey[value as ExitRuleKey]?.label ?? value}
                      wrapperStyle={{ fontSize: 12 }}
                    />
                    {RULE_ORDER.map((key) =>
                      rulesByKey[key] ? (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={RULE_COLOR[key]}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
                          isAnimationActive={false}
                        />
                      ) : null,
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No replayed signals to chart.</p>
            )}
          </section>

          {/* Data provenance */}
          <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm" aria-label="Data">
            <button
              type="button"
              className="font-semibold text-gray-900 hover:underline"
              onClick={() => setShowData((v) => !v)}
              aria-expanded={showData}
            >
              {showData ? 'Hide' : 'Show'} data provenance ({report.fetches.length} candle series,{' '}
              {report.signals.skipped.length} signal{report.signals.skipped.length === 1 ? '' : 's'} not replayable)
            </button>
            {showData && (
              <div className="mt-3 space-y-3 text-gray-600">
                <p>
                  Candles come from {report.exchange} over the engine&apos;s own market-data path. The venue serves at
                  most {report.signals.venue_ohlc_cap} candles per timeframe and cannot page further back, so a signal
                  whose open window is older than that on its timeframe is skipped, never invented. Production closes
                  on the exchange&apos;s last tick every five minutes; the replay closes on candle levels and closes.
                </p>
                <ul className="list-disc space-y-0.5 pl-5">
                  {report.fetches.map((f) => (
                    <li key={`${f.symbol}-${f.timeframe}`}>
                      {f.symbol} {f.timeframe}: {f.error ? `fetch failed (${f.error})` : `${f.candles} candles`}
                      {f.first_candle && ` from ${new Date(f.first_candle).toLocaleDateString()}`}
                      {` · signals ${f.signals.join(', ')}`}
                    </li>
                  ))}
                </ul>
                {report.signals.skipped.length > 0 && (
                  <ul className="list-disc space-y-0.5 pl-5">
                    {report.signals.skipped.map((s) => (
                      <li key={s.signal_id}>
                        #{s.signal_id} {s.symbol}: {s.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>

          {/* Per-signal table */}
          <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm" aria-label="Per-signal replay">
            <button
              type="button"
              className="font-semibold text-gray-900 hover:underline"
              onClick={() => setShowSignals((v) => !v)}
              aria-expanded={showSignals}
            >
              {showSignals ? 'Hide' : 'Show'} per-signal replay ({report.per_signal.length})
            </button>
            {showSignals && (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-left uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-2 py-1">Signal</th>
                      <th className="px-2 py-1">Entered</th>
                      <th className="px-2 py-1 text-right">Risk</th>
                      <th className="px-2 py-1">Recorded</th>
                      {RULE_ORDER.map((key) => (
                        <th key={key} className="px-2 py-1">
                          {rulesByKey[key]?.label ?? key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {report.per_signal.map((row) => (
                      <tr key={row.signal_id}>
                        <td className="px-2 py-1 whitespace-nowrap">
                          #{row.signal_id} {row.symbol} {row.direction.toUpperCase()} {row.timeframe}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">{new Date(row.entry_at).toLocaleString()}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{row.risk_pct.toFixed(2)}%</td>
                        <td className="px-2 py-1 whitespace-nowrap tabular-nums">
                          {row.recorded.reason ? reasonLabel[row.recorded.reason] ?? row.recorded.reason : 'n/a'}{' '}
                          {fmtR(row.recorded.net_r)}
                        </td>
                        {RULE_ORDER.map((key) => {
                          const o = row.rules[key]
                          return (
                            <td key={key} className="px-2 py-1 whitespace-nowrap tabular-nums">
                              {o ? `${reasonLabel[o.reason] ?? o.reason} ${fmtR(o.net_r)}${o.ambiguous ? ' *' : ''}` : '—'}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-gray-500">* candle spanned both levels; scored stop-first.</p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

const SignificanceBadge = ({ significant, n }: { significant: boolean; n: number }) => {
  if (n === 0) return <span className="text-gray-500">—</span>
  return significant ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" /> CI excludes zero
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
      <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" /> Not significant
    </span>
  )
}

export default AdminExitBacktest
